import type { OrderStatus } from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import * as patients from '@seen/core/models/patients'
import type { QueryTypes } from '@seen/core/search'
import { useMemo } from 'react'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

interface UseOrdersResult {
  data: Order[] | undefined
  isLoading: boolean
  error: Error | null
}

// Orders in these statuses are excluded from the default fetch and only
// loaded when explicitly opted in via the `extraStatuses` arg.
const OPT_IN_STATUSES = [
  'completed',
  'revoked',
] as const satisfies readonly OrderStatus[]

export function useOrders(extraStatuses: OrderStatus[] = []): UseOrdersResult {
  const showDemoPatientsOnly = usePortalStore(
    (state) => state.showDemoPatientsOnly
  )

  const filterQuery = useMemo<QueryTypes>(() => {
    const baseQuery: QueryTypes = showDemoPatientsOnly
      ? {
          ...Order.listPageQuery(),
          'patient._tag': patients.DEMO_TAG.code,
        }
      : Order.listPageQuery()

    return baseQuery
  }, [showDemoPatientsOnly])

  const { data, isLoading, error } = useSearchResourcePages(Order, filterQuery)

  const optedInStatuses = useMemo(
    () =>
      extraStatuses.filter((s): s is (typeof OPT_IN_STATUSES)[number] =>
        (OPT_IN_STATUSES as readonly OrderStatus[]).includes(s)
      ),
    [extraStatuses]
  )

  const optInQuery = useMemo<QueryTypes | undefined>(() => {
    if (optedInStatuses.length === 0) {
      return undefined
    }
    const base: QueryTypes = {
      ...Order.listPageQuery({ status: optedInStatuses.join(',') }),
    }
    if (showDemoPatientsOnly) {
      base['patient._tag'] = patients.DEMO_TAG.code
    }
    return base
  }, [optedInStatuses, showDemoPatientsOnly])

  const {
    data: optedInData,
    isLoading: isLoadingOptedIn,
    error: optedInError,
  } = useSearchResourcePages(Order, optInQuery)

  const mergedData = useMemo(() => {
    if (!data && !optedInData) {
      return undefined
    }
    return [...(data ?? []), ...(optedInData ?? [])]
  }, [data, optedInData])

  return {
    data: mergedData,
    isLoading: isLoading || (optedInStatuses.length > 0 && isLoadingOptedIn),
    error: error ?? optedInError,
  }
}
